import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export function SearchBar({
  value,
  onChange,
  placeholder,
}: SearchBarProps) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder || t('searchPlaceholder')
  const [localValue, setLocalValue] = useState(value)

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      onChange(localValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [localValue, onChange])

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleClear = () => {
    setLocalValue('')
    onChange('')
  }

  return (
    <div className="relative group">
      {/* Search Icon */}
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-emerald-400 transition-colors pointer-events-none" />

      {/* Input */}
      <Input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={resolvedPlaceholder}
        className="pl-10 pr-10 bg-neutral-900/50 border-white/[0.08] text-white placeholder:text-neutral-500 focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500/50 transition-all"
      />

      {/* Clear Button */}
      {localValue && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClear}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-all animate-in fade-in zoom-in duration-200"
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  )
}
